/** @format */

import React, { useEffect, useState, Suspense } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

import { FaBookOpen } from "react-icons/fa";

import Header from "../../components/common/Header";
import { ROUTES } from "../../constant/routes";
import { useSEO } from "../../hooks/useSEO";
import { injectJsonLd } from "../../utils/jsonLd";
import { lawSubjectsSchema } from "../../schema/lawSubjects.schema";

// lazy load footer
const Footer = React.lazy(() => import("../../components/common/Footer"));

interface Subject {
  subjectId: string;
  subjectCode: string;
  subjectName: string;
  description?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const AllSubject: React.FC = () => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const getSlug = (subject: Subject) =>
    subject.subjectName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/subjectallsubjects`);
        setSubjects(response?.data?.success || []);
      } catch (err: any) {
        setError(err.message || "Failed to fetch subjects");
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, []);

  // Dynamic keywords
  const dynamicKeywords =
    subjects.length > 0
      ? subjects.map((s) => s.subjectName).join(", ")
      : "LLB subjects, LLB law subjects, Law subjects list, LLB syllabus subjects";

  /* ---------------- SEO ---------------- */
  useSEO({
    title: "All Law Subjects for LLB Students | LLB360",
    description:
      "Explore all LLB law subjects including Constitutional Law, Criminal Law, Contract Law, Tort Law, Jurisprudence, and more. Ideal for LLB students and legal aspirants.",
    keywords: `LLB subjects, LLB law subjects, Law subjects list, List of law subjects, LLB syllabus subjects, All law subjects, Law subjects for LLB students, Law subjects name, Law courses subjects, Bachelor of Laws subjects, Law Study Material, ${dynamicKeywords}`,
    canonical: "https://www.llb360.com/law-subjects",

    ogTitle: "All Law Subjects for LLB Students | LLB360",
    ogDescription:
      "Complete list of LLB law subjects with structured explanations and learning resources.",
    ogSiteName: "LLB 360",
    ogImage: "https://www.llb360.com/og-law-subjects.webp",

    twitterTitle: "All Law Subjects for LLB Students | LLB360",
    twitterDescription:
      "Study all LLB law subjects with clear explanations and exam-oriented notes.",
    twitterImage: "https://www.llb360.com/og-law-subjects.webp",
  });

  // JSON-LD Schema
  useEffect(() => {
    injectJsonLd(lawSubjectsSchema);
  }, []);
  /* ------------------------------------- */

  return (
    <body className="bg-white p-0! overflow-x-hidden">
      <Header />

      <main className="container mx-auto px-4 py-8 flex-grow">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-950 to-black">
            Explore Our Subjects
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-500">
            Dive into our comprehensive collection of law subjects designed to
            empower your legal journey.
          </p>
        </div>

        {loading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-gray-500" />
          </div>
        )}

        {error && (
          <div className="text-center text-red-500 p-4 bg-red-50 rounded-lg max-w-lg mx-auto">
            {error}
          </div>
        )}

        {!loading && !error && subjects.length === 0 && (
          <div className="text-center text-gray-500 p-12">
            No subjects found at the moment.
          </div>
        )}

        {!loading && !error && subjects.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {subjects.map((subject) => (
              <Link
                key={subject.subjectId}
                to={ROUTES.SUBJECT_DETAIL(getSlug(subject))}
                state={{
                  subjectCode: subject.subjectCode,
                  subjectId: subject.subjectId,
                  subjectName: subject.subjectName,
                  description: subject.description,
                }}
                aria-label={`View details for ${subject.subjectName}`}
                className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all overflow-hidden flex flex-col"
              >
                <div className="p-6 flex flex-col flex-grow">
                  <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-full bg-gray-50 text-gray-600 group-hover:bg-gray-600 group-hover:text-white transition">
                    <FaBookOpen />
                  </div>

                  <h2 className="text-xl font-bold text-gray-800 group-hover:text-gray-600 mb-2 line-clamp-2">
                    {subject.subjectName}
                  </h2>

                  <p className="text-sm text-gray-600 line-clamp-3 flex-grow">
                    {subject.description}
                  </p>
                </div>

                <div className="px-6 py-4 bg-gray-50 border-t group-hover:bg-gray-50 transition">
                  <span className="text-sm font-medium text-gray-600">
                    View Details →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <Suspense fallback={<div className="h-20 bg-gray-100 animate-pulse" />}>
        <Footer />
      </Suspense>
    </body>
  );
};

export default AllSubject;
